// NodeTabsBar.jsx

import React, { useEffect } from 'react';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Hooks
import useUnifiedNodeTabs from 'node/hooks/useUnifiedNodeTabs';

// CSS
import './NodeTabsBar.css';

// Components
import NodeAddRelatedButton from 'node/addrelated/NodeAddRelatedButton';
import NodeToolbar from 'node/toolbar/NodeToolbar';

/**
 * NodeTabsBar
 *
 * @returns {ReactNode} The rendered component.
 */
const NodeTabsBar = React.memo(({
  node,
  nodeType,
  childNodes,
  activeTabKey,
  setActiveTabKey,
  onUserTabSelect = null,
  includeAboutTab = false,
  includeActivityTab = false,
  includeFormsTab = true,
  includeFloorPlanTab = true,
  forceHighlightTab = false,
  preferAboutTab = false,
  activityCount = 0,
  activityCommentCount = null,
  areChildNodesReady = true,
  isNodeLoading = false,
  persistSessionTab = true,
  showAddRelatedButton = true,
  tabsLayout = 'row',
  onQuickActionSelect,
  onSpecialAddSelect,
  onExistingAttached,
  onTabsChange = null,
  onDefaultTabPendingChange = null,
}) => {

  // ----- DEVICE DETECTION ----- //

  const isMobile = useIsMobile();

  // ----- HOOKS ----- //

  const { tabs, isDefaultTabPending } = useUnifiedNodeTabs({
    node,
    childNodes,
    nodeType,
    activeTabKey,
    setActiveTabKey,
    onUserTabSelect,
    isMobile,
    includeAboutTab,
    includeActivityTab,
    includeFormsTab,
    includeFloorPlanTab,
    forceHighlightTab,
    preferAboutTab,
    activityCount: activityCommentCount == null ? activityCount : activityCommentCount,
    areChildNodesReady,
    isNodeLoading,
    persistSessionTab,
  });

  // ----- EFFECTS ----- //

  // Optional mirror for experimental chrome (e.g. sections hamburger).
  useEffect(() => {
    if (typeof onTabsChange !== 'function') return;
    onTabsChange(Array.isArray(tabs) ? tabs : []);
  }, [onTabsChange, tabs]);

  useEffect(() => {
    if (typeof onDefaultTabPendingChange !== 'function') return;
    onDefaultTabPendingChange(Boolean(isDefaultTabPending));
  }, [isDefaultTabPending, onDefaultTabPendingChange]);

  // ----- RENDER ----- //

  return (
    <div className="node-tabs-bar">
      <NodeToolbar
        tabs={tabs}
        selectedTabKey={activeTabKey}
        isMobile={isMobile}
        layout={tabsLayout}
        addRelatedButton={showAddRelatedButton ? (
          <NodeAddRelatedButton
            node={node}
            iconOnly={tabsLayout === 'wrap'}
            onExistingAttached={onExistingAttached}
            onQuickActionSelect={onQuickActionSelect}
            onSpecialAddSelect={onSpecialAddSelect}
          />
        ) : null}
      />

    </div>
  );
});

// Dev Tools
NodeTabsBar.displayName = 'NodeTabsBar';

export default NodeTabsBar;
